import React, { useState } from "react";

import routes from "@utils/routes";

import { useScenarioAndAssociationsQuery } from "@api/scenarios/queries/useScenarioAndAssociationsQuery";
import useTimeValue from "@hooks/useTimeValue";
import Http from "@shared/http";

import {
  totalIncomeFromInvestments,
  totalContributions,
  totalCapital,
} from "@utils/assets";

import {
  totalNetIncomeAtAge,
  totalNetExpenseAtAge,
  totalLiabilityRepaymentsAtAge,
  totalAdditionalIncomeFromInvestmentsAtAge,
  totalMonthlyCashflowIncomeAtAge,
  totalMonthlyCashflowExpensesAtAge,
} from "@utils/forecast";

import { ActionablesProvider } from "@contexts/ActionablesContext";

import { Grid, GridArea } from "@UILibrary/Grid";

import { ForecastCard } from "@components/ForecastCard";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { Tab, Tabs, TabList, TabPanel } from "@components/Tabs";
import { MonthlyRetirementBudget } from "@components/MonthlyRetirementBudget";
import { AllocationsOverview } from "@components/AllocationsOverview";
import { SafeRetirementIndicator } from "@components/SafeRetirementIndicator";
import { CalculationConfig } from "@components/CalculationConfig";
import ConfigureScenarioModal from "@components/Modal/ConfigureScenarioModal";
import useFeatureConfig, { FeatureNames } from "@hooks/useFeatureConfig";

import { App } from "../App";
import { Assets } from "@containers/Assets";
import { CashflowEvents } from "@containers/CashflowEvents";
import { Liabilities } from "@containers/Liabilities";
import { Risk } from "@containers/Risk";
import { Notes } from "@containers/Notes";
import { FNA } from "@containers/FNA";
import { Budget } from "@containers/Budget";
import { RiskProducts } from "@containers/RiskProducts";

import { PlanActions } from "./components/PlanActions";
import { ScenarioHeader } from "./components/ScenarioHeader";
import { Assumptions } from "./components/Assumptions";
import { ProjectionDisclaimer } from "./components/ProjectionDisclaimer";
import { LoadingStatusIndicator } from "./components/LoadingStatusIndicator";
import { InviteCard } from "./components/InviteCard";
import { ScenarioNotifications } from "./components/ScenarioNotifications";
import { MobileCta } from "./components/MobileCta";
import { FrozenScenario } from "./components/FrozenScenario";

import styles from "./Scenario.module.scss";
import { useNoteTemplatesQuery } from "../../api/scenarios/queries/useNoteTemplatesQuery";

export const Scenario = (scenarioData) => {
  const config = scenarioData.config;
  const historic = scenarioData.scenario.baseScenario.planType === "historic";

  return (
    <App
      config={scenarioData.config}
      // NOTE: Revisit how we are going to manage the
      // showTalkToAWealthManager flow
      showTalkToAWealthManager={false}
    >
      <ActionablesProvider
        location="implementedPlan"
        planType={scenarioData.scenario.baseScenario.planType}
        tenant={config.tenant}
        userDetails={config.userDetails}
        featureToggles={config.featureToggles}
        // NOTE: Revisit how we are going to manage the
        // showTalkToAWealthManager flow
        showTalkToAWealthManager={false}
      >
        <ErrorBoundary
          context={{
            tenant: scenarioData.config.tenant,
            userDetails: scenarioData.config.userDetails,
          }}
        >
          {historic ? (
            <FrozenScenario {...scenarioData} />
          ) : (
            <ScenarioComponents {...scenarioData} />
          )}
        </ErrorBoundary>
      </ActionablesProvider>
    </App>
  );
};

const ScenarioComponents = (scenarioData) => {
  const {
    scenario,
    assets,
    forecast,
    cashflowEvents,
    liabilities,
    riskProfile,
    riskProducts,
    notes,
    budget,
    fna,
    assetAllocation,
    isFetching,
    loadingIndicators,
  } = useScenarioAndAssociationsQuery({
    tenantSlug: scenarioData.config.tenant.slug,
    scenarioID: scenarioData.scenario.baseScenario.id,
    defaultValues: scenarioData,
  });

  const noteTemplates = useNoteTemplatesQuery({
    scenarioID: scenarioData.scenario.baseScenario.id,
    tenantSlug: scenarioData.config.tenant.slug,
    defaultValues: [],
  });
  const [
    { isEnabled: isAssetEventsEnabled },
    { isEnabled: isJointPlanningEnabled },
  ] = useFeatureConfig(
    FeatureNames.ASSET_EVENT__ALL,
    FeatureNames.JOINT_PLAN__VIEW
  );

  const { timeValue, setTimeValue, usePresentValue } = useTimeValue({
    tenantSlug: scenarioData.config.tenant.slug,
    clientId: scenarioData.config.userDetails.client_id,
    defaultValue: scenarioData.config.timeValue,
  });

  const projectionDisclaimer = scenarioData.config.tenant.projectionDisclaimer;
  // Every "create a new scenario" entry point opens the copy modal, so the
  // user always chooses whether notes come across.
  const [createScenarioModalOpen, setCreateScenarioModalOpen] = useState(false);
  const onCreateNewScenario = () => setCreateScenarioModalOpen(true);

  const createScenario = async (optional_sections) => {
    const params = {
      source_scenario_id: scenarioData.scenario.baseScenario.id,
      optional_sections,
    };

    const response = await Http.request(
      routes.createScenarioPath({
        tenantSlug: scenarioData.config.tenant.slug,
      }),
      "POST",
      JSON.stringify(params)
    );
    if (!response.ok) throw new Error("Could not create the scenario");

    const { redirectPath } = await response.json();
    if (redirectPath) {
      window.location = redirectPath;
    }
  };

  return (
    <Grid className={styles.scenarioLayout}>
      <LoadingStatusIndicator
        isFetching={isFetching}
        loadingIndicators={loadingIndicators}
      />
      <ConfigureScenarioModal
        open={createScenarioModalOpen}
        onClose={() => setCreateScenarioModalOpen(false)}
        onCreateNewScenario={createScenario}
      />
      <GridArea name="notifications">
        <ScenarioNotifications
          clientHasId={
            scenarioData.config.userDetails.clientHasIdentificationNumber
          }
          isWealthManager={scenarioData.config.userDetails.isWealthManager}
          editClientPath={routes.editClientPath({
            tenantSlug: scenarioData.config.tenant.slug,
            clientId: scenarioData.config.userDetails.client_id,
          })}
          jointScenarioPath={
            scenarioData.config.userDetails.jointScenarioId &&
            isJointPlanningEnabled() &&
            routes.jointScenarioPath({
              tenantSlug: scenarioData.config.tenant.slug,
              clientId: scenarioData.config.userDetails.client_id,
              jointScenarioId: scenarioData.config.userDetails.jointScenarioId,
            })
          }
        />
      </GridArea>
      <GridArea name="forecast">
        <ScenarioHeader
          label={"Plan name"}
          scenario={{
            name: scenario.data.baseScenario.name || "My plan",
            status: scenario.data.baseScenario.status,
            planType: scenario.data.baseScenario.planType,
          }}
          updateScenario={scenario.actions.update}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
        />

        <ForecastCard
          colors={scenarioData.config.colors}
          clientPronoun={scenarioData.config.userDetails.clientPronoun}
          assumptions={scenario.data.baseScenario.assumptions}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          forecasts={
            forecast.data.comparedScenario
              ? {
                  actualForecast: forecast.data.comparedScenario.forecast,
                  scenarioForecast: forecast.data.baseScenario.forecast,
                }
              : {
                  actualForecast: forecast.data.baseScenario.forecast,
                }
          }
          showTicksInYear={false}
          cashflowEvents={cashflowEvents.data}
          retirementAge={scenario.data.baseScenario.retirementAge}
          currentAge={scenarioData.config.userDetails.clientCurrentAge}
          expectedAge={scenario.data.baseScenario.expectedAge}
        />
      </GridArea>

      <GridArea name="aside">
        <MobileCta
          sendLoginLinkPath={routes.sendLoginLinkPath({
            tenantSlug: scenarioData.config.tenant.slug,
            clientId: scenarioData.config.userDetails.client_id,
          })}
          onCreateNewScenario={onCreateNewScenario}
          tenantSlug={scenarioData.config.tenant.slug}
          scenario={{
            planType: scenario.data.baseScenario.planType,
          }}
        />
        <PlanActions
          scenario={{
            id: scenario.data.baseScenario.id,
            name: scenario.data.baseScenario.name,
            status: scenario.data.baseScenario.status,
            planType: scenario.data.baseScenario.planType,
          }}
          assets={assets.data}
          riskProfile={riskProfile.data.baseRiskProfile}
          comparedRiskProfile={riskProfile.data.comparedRiskProfile}
          updateScenario={scenario.actions.update}
          tenantSlug={scenarioData.config.tenant.slug}
          clientId={scenarioData.config.userDetails.client_id}
          clientHasIdentificationNumber={
            scenarioData.config.userDetails.clientHasIdentificationNumber
          }
          clientCellphoneNumber={
            scenarioData.config.userDetails.client_cellphone_number
          }
          usePresentValue={usePresentValue}
          onCreateNewScenario={onCreateNewScenario}
          existingAstuteRequest={
            scenarioData.config.userDetails.clientAstuteRequestExists
          }
          jointScenarioId={scenarioData.config.userDetails.jointScenarioId}
          isClient={scenarioData.config.userDetails.isClient}
          userDetails={{
            profileId: scenarioData.config.userDetails.profile_id,
          }}
        />

        <SafeRetirementIndicator>
          <SafeRetirementIndicator.Heading />
          <SafeRetirementIndicator.Indicator
            safeRetirementStatus={
              forecast.data.baseScenario.safeRetirementStatus
            }
          />
          <SafeRetirementIndicator.CapitalRequirementsCardExtension
            capitalProps={{
              capitalForPossibly:
                forecast.data.baseScenario.capitalRequirements
                  .capitalForSafeRetirementBrackets.possibly,
              capitalForLikely:
                forecast.data.baseScenario.capitalRequirements
                  .capitalForSafeRetirementBrackets.likely,
              capitalAtRetirement:
                forecast.data.baseScenario.capitalRequirements
                  .projectedCapitalAtRetirement,
              inflation: scenario.data.baseScenario.assumptions.inflation,
              yearsToRetirement:
                scenario.data.baseScenario.retirementAge -
                scenarioData.config.userDetails.clientCurrentAge,
              timeValue: timeValue,
            }}
          />
        </SafeRetirementIndicator>

        {isAssetEventsEnabled() ? (
          <CalculationConfig assets={assets.data} />
        ) : null}

        <Assumptions
          updateScenario={scenario.actions.updateWithForecastInvalidation}
          clientCurrentAge={scenarioData.config.userDetails.clientCurrentAge}
          baseScenario={{
            status: scenario.data.baseScenario.status,
            planType: scenario.data.baseScenario.planType,
            retirementAge: scenario.data.baseScenario.retirementAge,
            expectedAge: scenario.data.baseScenario.expectedAge,
            maxExpectedAge:
              scenario.data.baseScenario.assumptions.clientMaxExpectedAge,
            inflation: scenario.data.baseScenario.assumptions.inflation,
            annualIncreaseInMonthlyContribution:
              scenario.data.baseScenario.annualIncreaseInMonthlyContribution,
          }}
          comparedScenario={
            scenario.data.comparedScenario
              ? {
                  retirementAge: scenario.data.comparedScenario.retirementAge,
                  annualIncreaseInMonthlyContribution:
                    scenario.data.comparedScenario
                      .annualIncreaseInMonthlyContribution,
                  expectedAge: scenario.data.comparedScenario.expectedAge,
                }
              : {}
          }
        />
      </GridArea>

      <GridArea name="budget">
        <MonthlyRetirementBudget
          style={{ height: "100%", minHeight: "25rem" }}
          budgetType={budget.data.budgetType}
          {...(forecast.data.comparedScenario
            ? {
                baseScenario: {
                  desiredRetirementIncome:
                    forecast.data.baseScenario.budget.desiredRetirementIncome,
                  actualRetirementIncome:
                    forecast.data.baseScenario.budget
                      .maximumSafeRetirementIncome,
                },
                comparedScenario: {
                  desiredRetirementIncome:
                    forecast.data.comparedScenario.budget
                      .desiredRetirementIncome,
                  actualRetirementIncome:
                    forecast.data.comparedScenario.budget
                      .maximumSafeRetirementIncome,
                },
              }
            : {
                baseScenario: {
                  desiredRetirementIncome:
                    forecast.data.baseScenario.budget.desiredRetirementIncome,
                  actualRetirementIncome:
                    forecast.data.baseScenario.budget
                      .maximumSafeRetirementIncome,
                },
              })}
        />
      </GridArea>

      <GridArea name="invite" className="md:hidden">
        <InviteCard
          referralsPath={routes.referralsPath({
            tenantSlug: scenarioData.config.tenant.slug,
            clientId: scenarioData.config.userDetails.client_id,
          })}
        />
      </GridArea>

      <GridArea name="allocations" className="hidden lg:block">
        <AllocationsOverview
          style={{ height: "100%" }}
          assetAllocationsDataset={
            assetAllocation.data.comparedScenario
              ? [
                  assetAllocation.data.baseScenario.assetAllocation,
                  assetAllocation.data.comparedScenario.assetAllocation,
                ]
              : [assetAllocation.data.baseScenario.assetAllocation]
          }
        />
      </GridArea>

      <GridArea name="tabs">
        <Tabs defaultIndex={0} forceRenderTabPanel className="card-tabs">
          <TabList className="card-tabs__list overflow-x-scroll lg:overflow-auto [&>li]:flex [&>li]:items-center">
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Assets</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Cashflow</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Budget</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item hidden md:inline-block"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Liabilities</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item hidden md:inline-block"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Risk profile</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item hidden md:inline-block"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Risk products</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>Notes</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item hidden md:inline-block"
              selectedClassName="card-tabs__list-item--selected"
            >
              <h2>FNA</h2>
            </Tab>
          </TabList>
          <TabPanel>
            <Assets
              scenario={{
                id: scenario.data.baseScenario.id,
                planType: scenario.data.baseScenario.planType,
                retirementAge: scenario.data.baseScenario.retirementAge,
                expectedAge: scenario.data.baseScenario.expectedAge,
                isRetired: scenario.data.baseScenario.isRetired,
              }}
              assets={assets.data}
              crudActions={assets.actions}
              onCreateNewScenario={onCreateNewScenario}
              totalContributions={totalContributions(
                assets.data,
                scenarioData.config.supportedCurrencies
              )}
              totalCapital={totalCapital(
                assets.data,
                scenarioData.config.supportedCurrencies
              )}
              riskProfile={riskProfile.data.baseRiskProfile}
              supportedCurrencies={scenarioData.config.supportedCurrencies}
              serviceProviders={scenarioData.config.serviceProviders}
              assumptions={scenario.data.baseScenario.assumptions}
              assetView={scenarioData.assetView}
              tenant={scenarioData.config.tenant}
              featureToggles={scenarioData.config.featureToggles}
              featureConfig={scenarioData.config.featureConfig}
              userDetails={scenarioData.config.userDetails}
            />
          </TabPanel>
          <TabPanel>
            <CashflowEvents
              scenario={{
                id: scenario.data.baseScenario.id,
                expectedAge: scenario.data.baseScenario.expectedAge,
              }}
              cashflowEvents={cashflowEvents.data}
              crudActions={cashflowEvents.actions}
              onCreateNewScenario={onCreateNewScenario}
              riskProfile={riskProfile.data.baseRiskProfile}
              userDetails={scenarioData.config.userDetails}
              assumptions={scenario.data.baseScenario.assumptions}
              currentAge={scenarioData.config.userDetails.clientCurrentAge}
              expectedAge={scenario.data.baseScenario.expectedAge}
              // NOTE: How necesary is this, and can we remove the liquidity checks
              // This is used to come up with an estimation if an expense event
              // has enough liquid capital to execute
              editedProjectionData={forecast.data.baseScenario.forecast}
            />
          </TabPanel>
          <TabPanel>
            <Budget
              budget={budget.data}
              totalIncomeFromInvestments={totalIncomeFromInvestments(
                assets.data,
                scenarioData.config.supportedCurrencies
              )}
              totalAdditionalIncomeFromInvestments={totalAdditionalIncomeFromInvestmentsAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              totalContributions={totalContributions(
                assets.data,
                scenarioData.config.supportedCurrencies
              )}
              totalLiabilityRepayments={totalLiabilityRepaymentsAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              totalCurrentNetIncome={totalNetIncomeAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              totalCurrentNetExpense={totalNetExpenseAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              totalMonthlyCashflowIncome={totalMonthlyCashflowIncomeAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              totalMonthlyCashflowExpenses={totalMonthlyCashflowExpensesAtAge(
                forecast.data.baseScenario.forecast,
                scenarioData.config.userDetails.clientCurrentAge
              )}
              budgetCategories={scenarioData.config.budgetCategories}
              minOldAgePension={scenarioData.config.minOldAgePension}
              onCreateNewScenario={onCreateNewScenario}
              updateBudgetHandler={budget.actions.update}
              isRetired={scenario.data.baseScenario.isRetired}
            />
          </TabPanel>
          <TabPanel>
            <Liabilities
              scenario={{
                id: scenario.data.baseScenario.id,
              }}
              liabilities={liabilities.data}
              crudActions={liabilities.actions}
              onCreateNewScenario={onCreateNewScenario}
              tenant={scenarioData.config.tenant}
              supportedLiabilityTypes={
                scenarioData.config.supportedLiabilityTypes
              }
              featureToggles={scenarioData.config.featureToggles}
            />
          </TabPanel>
          <TabPanel>
            <Risk
              riskProfile={riskProfile.data.baseRiskProfile}
              crudActions={riskProfile.actions}
              onCreateNewScenario={onCreateNewScenario}
              tenant={scenarioData.config.tenant.slug}
              isRetired={scenario.data.baseScenario.isRetired}
              clientPronoun={scenarioData.config.userDetails.clientPronoun}
              inflation={scenario.data.baseScenario.assumptions.inflation}
              yearsToRetirement={scenario.data.baseScenario.yearsToRetirement}
              isWealthManager={scenarioData.config.userDetails.isWealthManager}
              useExternalRiskProfiling={false}
            />
          </TabPanel>
          <TabPanel>
            <RiskProducts
              scenario={{
                id: scenario.data.baseScenario.id,
              }}
              riskProducts={riskProducts.data}
              crudActions={riskProducts.actions}
              onCreateNewScenario={onCreateNewScenario}
              tenant={scenarioData.config.tenant}
              supportedRiskProductTypes={
                scenarioData.config.supportedRiskProductTypes
              }
              serviceProviders={scenarioData.config.serviceProviders}
            />
          </TabPanel>
          <TabPanel>
            <Notes
              notes={notes.data}
              noteTemplates={noteTemplates.data}
              withTemplates={scenarioData.config.featureToggles.noteTemplates}
              isScenarioPlanning={
                scenario.data.baseScenario.planType === "implemented"
              }
              crudActions={notes.actions /* Using crudActions directly here */}
            />
          </TabPanel>
          <TabPanel>
            <FNA
              fna={fna.data}
              crudActions={fna.actions}
              assets={assets.data}
              liabilities={liabilities.data}
              budget={budget.data}
              riskProducts={riskProducts.data}
              supportedCurrencies={scenarioData.config.supportedCurrencies}
              onCreateNewScenario={onCreateNewScenario}
            />
          </TabPanel>
        </Tabs>
      </GridArea>
      <GridArea name="disclaimer">
        <ProjectionDisclaimer projectionDisclaimer={projectionDisclaimer} />
      </GridArea>
    </Grid>
  );
};
